import React, { useState } from 'react';
import { X, Upload, Package, Loader } from 'lucide-react';
import type { CourierPackage } from '@shared/sdk-v1';
import { apiConfig } from '../shared/api-config';
import { showSuccessToast, showErrorToast } from '../utils/toast';

interface ClaimPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  pkg: CourierPackage;
  warehouseCourierId: number;
  onSubmit: (
    packageId: number,
    warehouseCourierId: number,
    invoiceFile: string[],
    notes: string
  ) => Promise<void>;
}

const ClaimPackageModal: React.FC<ClaimPackageModalProps> = ({
  isOpen,
  onClose,
  pkg,
  warehouseCourierId,
  onSubmit,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const uploadFiles = async (): Promise<string[]> => {
    if (files.length === 0) return [];

    const config = apiConfig();
    const uploadedNames: string[] = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${config.basePath}/Uploads/claims-${warehouseCourierId}/upload`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${await config.accessToken?.()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const data = await response.json();
      const fileName = data.result?.files?.file?.[0]?.name;
      if (fileName) {
        uploadedNames.push(fileName);
      }
    }

    return uploadedNames;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pkg.id || files.length === 0) return;

    setIsSubmitting(true);
    try {
      // Upload files first
      const uploadedFileNames = await uploadFiles();

      // Submit the claim
      await onSubmit(pkg.id, warehouseCourierId, uploadedFileNames, notes);

      showSuccessToast({
        title: 'Claim Submitted',
        message: 'Your claim has been submitted for review',
      });

      // Reset and close
      setFiles([]);
      setNotes('');
      onClose();
    } catch (err: any) {
      showErrorToast({
        title: 'Error',
        message: err.message || 'Failed to submit claim',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Claim Package
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Package Info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                {pkg.trackingNo}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Description</span>
              <span className="text-gray-900 dark:text-white">
                {pkg.description || '-'}
              </span>
              <span className="text-gray-500 dark:text-gray-400">Weight</span>
              <span className="text-gray-900 dark:text-white">
                {pkg.weight ? `${pkg.weight} lbs` : '-'}
              </span>
              <span className="text-gray-500 dark:text-gray-400">Merchant</span>
              <span className="text-gray-900 dark:text-white">
                {pkg.merchant || '-'}
              </span>
            </div>
          </div>

          {/* Claim Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Invoice / Proof of Purchase <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="text-center">
                  <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <label
                    htmlFor="claim-file-upload"
                    className="cursor-pointer text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Choose files
                    <input
                      id="claim-file-upload"
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    PDF or images (max 10MB each)
                  </p>
                </div>
                {files.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {files.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-sm bg-gray-100 dark:bg-gray-700 rounded px-2 py-1"
                      >
                        <span className="truncate text-gray-700 dark:text-gray-300">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFiles((prev) => prev.filter((_, i) => i !== idx))
                          }
                          className="text-gray-400 hover:text-red-500 ml-2"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                placeholder="Why do you believe this package belongs to your courier? Any additional details..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || files.length === 0}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Claim'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClaimPackageModal;
